import { useDispatch } from 'react-redux';
import axios from 'axios';
import {
    createPriceOffer,
    send2FaCode,
    validate2FaCode,
    validatePromotionPackage,
} from '../services/promotionApi';
import {
    setNumberOfTriesLeft,
    setCodeValidMinutes,
    setCustomersContacts,
    setErrCode,
    setErrSource,
    setFlowToken,
    setIsFail,
    setIsLoading,
    setPackageInfo,
    setPromotionPackage,
    ErrSource,
} from '../store/mainSlice';
import { PromotionPackageInfoJson, PromotionPackageResponseCodes } from '../types/promotion';

export const usePromotionFlow = () => {
    const dispatch = useDispatch();

    const runFlowRequest = async <T extends { response: PromotionPackageResponseCodes; flowToken: string }>(
        source: ErrSource,
        request: () => Promise<T>,
        onResult?: (result: T) => void,
    ): Promise<T | null> => {
        try {
            dispatch(setIsLoading(true));
            const result = await request();
            onResult?.(result);
            if (result.response !== PromotionPackageResponseCodes.Success) {
                dispatch(setIsFail(true));
                dispatch(setErrCode(result.response));
                dispatch(setErrSource(source));
                return null;
            }
            dispatch(setIsFail(false));
            dispatch(setFlowToken(result.flowToken));
            return result;
        } catch (e) {
            dispatch(setIsFail(true));
            const status = axios.isAxiosError(e) ? e.response?.status : undefined;
            dispatch(setErrCode(status ?? PromotionPackageResponseCodes.GeneralError));
            dispatch(setErrSource(source));
            return null;
        } finally {
            dispatch(setIsLoading(false));
        }
    };

    const validatePackage = async (promotionPackageToken: string) => {
        const result = await runFlowRequest('validatePackage', () => validatePromotionPackage(promotionPackageToken));
        if (result) {
            const parsed = JSON.parse(result.infoJson) as Partial<PromotionPackageInfoJson>;
            dispatch(setPackageInfo({
                promoTitle: parsed.promoTitle ?? '',
                promoHeader: parsed.promoHeader ?? '',
                description: parsed.description ?? '',
                notes: parsed.notes ?? [],
            }));
        }
        return result;
    };

    const sendCode = async (flowToken: string, phoneNumber: string) => {
        const result = await runFlowRequest('sendCode', () => send2FaCode(flowToken, phoneNumber));
        if (result) {
            dispatch(setCodeValidMinutes(result.twoFaExpirationByMinutes));
        }
        return result;
    };

    const verifyCode = async (flowToken: string, code: string) => {
        const result = await runFlowRequest(
            'verifyCode',
            () => validate2FaCode(flowToken, code),
            (r) => dispatch(setNumberOfTriesLeft(r.numberOfTriesLeft)),
        );
        if (result) {
            dispatch(setCustomersContacts(result.customersContacts));
            dispatch(setPromotionPackage(result.promotionPackage));
        }
        return result;
    };

    const createOffer = (flowToken: string, contactToken: string) =>
        runFlowRequest('createOffer', () => createPriceOffer(flowToken, contactToken));

    return { validatePackage, sendCode, verifyCode, createOffer };
};
